'use client';

import FaIcon, { faClock, faTruck } from '@/components/icon/FaIcon/FaIcon';
import { localeAtom } from '@/features/lottery/atoms/localAtom';
import {
  estimatedShippingDateText,
  eventStatusLabel,
  hasShippingFee,
  showEstimatedShippingDate,
} from '@/features/lottery/utils';
import { Format } from '@/lib/dateFns';
import utilsStyles from '@/styles/modules/utils.module.scss';
import { formatCurrency, formatDateCommon } from '@/utils/formatters';
import { useAtom } from 'jotai';
import type { LotteryBenefit, LotteryCustomOption, LotteryPrice } from '../../types';
import ShippingAlert from '../ShippingAlert/ShippingAlert';
import styles from './LotteryDetailHeader.module.scss';

function LotteryDetailHeader({
  name,
  name_en,
  startAt,
  endAt,
  status,
  benefit,
  isCampaign = false,
  showSpKv = false,
  estimatedShippingDate,
  additionalShippingFeePerTimes,
  price,
  customOptions,
}: {
  name: string;
  name_en?: string;
  startAt: string;
  endAt: string;
  status: string;
  benefit: Array<LotteryBenefit>;
  isGlobal?: boolean;
  isCampaign?: boolean;
  showSpKv?: boolean;
  estimatedShippingDate?: string | null;
  additionalShippingFeePerTimes?: number;
  price?: LotteryPrice;
  customOptions?: LotteryCustomOption;
}) {
  const [locale] = useAtom(localeAtom);
  const isShowEstimatedShippingDate = () => {
    if (customOptions?.hideEstimatedShipmentDays === '1') {
      return false;
    }

    // シリアルコードでは一旦非表示
    return !isCampaign && showEstimatedShippingDate(endAt, estimatedShippingDate);
  };
  return (
    <header className={styles.componentStyle}>
      {/* /開催中、開催予定等の表示ラベル部分の制御 */}
      {showSpKv && (
        <div className={styles.itemStatus}>
          <img src={eventStatusLabel(status)} alt='' />
        </div>
      )}

      <p className={`${styles.itemTitle} ${utilsStyles.textBold}`}>{locale === 'en' ? name_en : name}</p>
      <div className={styles.labelArea}>
        {isCampaign ? (
          <div className={styles.campaignLabel}>
            <span className={utilsStyles.textBold}>キャンペーンくじ</span>
          </div>
        ) : (
          <div className={styles.benefitList}>
            {customOptions?.isPickujiDPayOnly ? (
              <p className={`${styles.benefitListItem} ${utilsStyles.textBold}`}>d払い決済限定特典あり</p>
            ) : (
              benefit.length > 0 && <p className={`${styles.benefitListItem} ${utilsStyles.textBold}`}>特典あり</p>
            )}
          </div>
        )}
      </div>
      <div className={`${styles.lotteryDate} ${utilsStyles.textBold}`}>
        <FaIcon icon={faClock} />
        &nbsp;
        {formatDateCommon(startAt, Format.dateTimeLotteryJa)}~{formatDateCommon(endAt, Format.dateTimeLotteryJa)}
      </div>
      {/* {showEstimatedShippingDate(endAt, estimatedShippingDate) && ( */}
      {isShowEstimatedShippingDate() && (
        <div className={`${styles.estimateShippingDate} ${utilsStyles.textBold}`}>
          <FaIcon icon={faTruck} />
          &nbsp;
          {estimatedShippingDateText({ eventEndAt: endAt, estimatedShippingDate: estimatedShippingDate || undefined })}
        </div>
      )}
      {drawFeeAnnotation(price)}
      {shippingAlert(price, additionalShippingFeePerTimes)}
    </header>
  );
}

function drawFeeAnnotation(price?: LotteryPrice) {
  if (!price) return <></>;
  if (price.drawFee === 0) return <></>;
  return (
    <div className={`${utilsStyles.flexRowNowrap} ${utilsStyles.alignCenter} ${styles.feeTextContainer}`}>
      <p className={styles.drawFeeText}>1回 {formatCurrency(price.drawFee)}円（税込）&nbsp;</p>
      <p className={utilsStyles.textXSmall}>
        /&nbsp;
        {hasShippingFee(price.shippingFee, price.additionalShippingFee)
          ? '（配送手数料を含まない）'
          : '（配送手数料を含む）'}
      </p>
    </div>
  );
}

function shippingAlert(price?: LotteryPrice, additionalShippingFeePerTimes?: number) {
  if (!price) return <></>;
  if (price.shippingFee === 0) return <></>;
  if (!hasShippingFee(price.shippingFee, price.additionalShippingFee)) return <></>;
  return (
    <div className={styles.feeTextContainer}>
      <ShippingAlert price={price} additionalShippingFeePerTimes={additionalShippingFeePerTimes} />
    </div>
  );
}

export default LotteryDetailHeader;
